import { useState } from 'react';
import { Plus, Trash2, GripVertical, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useBookableZones, BookableZone } from '@/hooks/useBookableZones';
import { ZoneScheduleSettings } from '@/components/ZoneScheduleSettings';

interface ZoneManagementCardProps {
  establishmentId: string;
  zonesEnabled: boolean;
  zonesSelectionMode: 'single' | 'multi';
  zonesHelpText: string;
  zonesStrict: boolean;
  onUpdateSettings: (params: {
    enabled?: boolean;
    selectionMode?: 'single' | 'multi';
    helpText?: string;
    strict?: boolean;
  }) => Promise<void>;
}

const DEFAULT_COLORS = [
  '#6366f1', '#ec4899', '#f59e0b', '#10b981',
  '#3b82f6', '#8b5cf6', '#ef4444', '#14b8a6',
];

export function ZoneManagementCard({
  establishmentId,
  zonesEnabled,
  zonesSelectionMode,
  zonesHelpText,
  zonesStrict,
  onUpdateSettings,
}: ZoneManagementCardProps) {
  const { toast } = useToast();
  const { zones, createZone, updateZone, deleteZone } = useBookableZones({ establishmentId });
  const [newZoneName, setNewZoneName] = useState('');
  const [newZoneColor, setNewZoneColor] = useState(DEFAULT_COLORS[0]);
  const [zoneToDelete, setZoneToDelete] = useState<BookableZone | null>(null);

  const handleCreate = async () => {
    if (!newZoneName.trim()) return;
    try {
      await createZone.mutateAsync({ name: newZoneName, color: newZoneColor });
      setNewZoneName('');
      setNewZoneColor(DEFAULT_COLORS[0]);
      toast({ title: 'Zone créée', description: `${newZoneName} a été ajoutée.` });
    } catch (err: any) {
      toast({ title: 'Erreur', description: err.message ?? 'Création impossible.', variant: 'destructive' });
    }
  };

  const handleConfirmDelete = async () => {
    if (!zoneToDelete) return;
    try {
      await deleteZone.mutateAsync(zoneToDelete.id);
      toast({ title: 'Zone supprimée' });
    } catch (err: any) {
      toast({ title: 'Erreur', description: err.message, variant: 'destructive' });
    } finally {
      setZoneToDelete(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span className="text-xl">🏷️</span>
          Zones réservables
        </CardTitle>
        <CardDescription>
          Définissez des zones nommées (ex. Bistrot, Salon, Bar) pour permettre à vos clients de choisir où s'asseoir
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="zonesEnabled">Activer la préférence de zone</Label>
            <p className="text-xs text-muted-foreground">
              Les clients pourront choisir une zone (ou plusieurs) lors de la réservation
            </p>
          </div>
          <Switch
            id="zonesEnabled"
            checked={zonesEnabled}
            onCheckedChange={async (checked) => {
              try {
                await onUpdateSettings({ enabled: checked });
                toast({
                  title: checked ? 'Zones activées' : 'Zones désactivées',
                });
              } catch {
                toast({ title: 'Erreur', variant: 'destructive' });
              }
            }}
          />
        </div>

        {zonesEnabled && (
          <div className="space-y-4 pt-4 border-t">
            {/* Selection mode */}
            <div className="space-y-2">
              <Label>Mode de sélection côté client</Label>
              <RadioGroup
                value={zonesSelectionMode}
                onValueChange={async (v) => {
                  try {
                    await onUpdateSettings({ selectionMode: v as 'single' | 'multi' });
                    toast({ title: 'Mode mis à jour' });
                  } catch {
                    toast({ title: 'Erreur', variant: 'destructive' });
                  }
                }}
                className="flex flex-col gap-2"
              >
                <label className="flex items-start gap-2 p-3 border rounded-lg cursor-pointer hover:border-primary/50">
                  <RadioGroupItem value="single" id="mode-single" className="mt-1" />
                  <div className="space-y-0.5">
                    <div className="font-medium text-sm">Choix unique</div>
                    <div className="text-xs text-muted-foreground">Le client choisit une seule zone (ou aucune préférence)</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 p-3 border rounded-lg cursor-pointer hover:border-primary/50">
                  <RadioGroupItem value="multi" id="mode-multi" className="mt-1" />
                  <div className="space-y-0.5">
                    <div className="font-medium text-sm">Sélection multiple</div>
                    <div className="text-xs text-muted-foreground">Le client peut accepter plusieurs zones</div>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {/* Help text */}
            <div className="space-y-2">
              <Label htmlFor="zonesHelpText">Texte d'aide affiché aux clients</Label>
              <Textarea
                id="zonesHelpText"
                defaultValue={zonesHelpText}
                placeholder="Préférez-vous une zone particulière ?"
                rows={2}
                onBlur={async (e) => {
                  const newText = e.target.value.trim();
                  if (newText && newText !== zonesHelpText) {
                    try {
                      await onUpdateSettings({ helpText: newText });
                      toast({ title: 'Texte mis à jour' });
                    } catch {
                      toast({ title: 'Erreur', variant: 'destructive' });
                    }
                  }
                }}
              />
            </div>

            {/* Strict mode */}
            <div className="flex items-center justify-between py-2">
              <div className="space-y-1">
                <Label htmlFor="zonesStrict">Mode strict</Label>
                <p className="text-xs text-muted-foreground">
                  Si activé, aucune table en dehors des zones choisies ne sera proposée. Sinon, fallback possible.
                </p>
              </div>
              <Switch
                id="zonesStrict"
                checked={zonesStrict}
                onCheckedChange={async (checked) => {
                  try {
                    await onUpdateSettings({ strict: checked });
                  } catch {
                    toast({ title: 'Erreur', variant: 'destructive' });
                  }
                }}
              />
            </div>

            {/* Zones list */}
            <div className="space-y-2 pt-2 border-t">
              <Label>Zones définies ({zones.length})</Label>
              {zones.length === 0 ? (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    Aucune zone définie. Ajoutez votre première zone ci-dessous.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-2">
                  {zones.map((zone) => (
                    <div
                      key={zone.id}
                      className="p-2 border rounded-lg bg-muted/30"
                    >
                      <div className="flex items-center gap-2">
                        <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div
                          className="w-6 h-6 rounded shrink-0 border"
                          style={{ backgroundColor: zone.color }}
                        />
                        <Input
                          defaultValue={zone.name}
                          className="flex-1 h-8"
                          onBlur={async (e) => {
                            const newName = e.target.value.trim();
                            if (newName && newName !== zone.name) {
                              try {
                                await updateZone.mutateAsync({ id: zone.id, name: newName });
                              } catch (err: any) {
                                toast({ title: 'Erreur', description: err.message, variant: 'destructive' });
                              }
                            }
                          }}
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive"
                          onClick={() => setZoneToDelete(zone)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <ZoneScheduleSettings
                        establishmentId={establishmentId}
                        zoneId={zone.id}
                        zoneName={zone.name}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add zone */}
            <div className="space-y-2 pt-2 border-t">
              <Label>Ajouter une zone</Label>
              <div className="flex gap-2">
                <Input
                  value={newZoneName}
                  onChange={(e) => setNewZoneName(e.target.value)}
                  placeholder="Ex: Bistrot, Salon, Bar"
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreate();
                  }}
                />
                <Button onClick={handleCreate} disabled={!newZoneName.trim() || createZone.isPending}>
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter
                </Button>
              </div>
              <div className="flex gap-1.5 pt-1">
                {DEFAULT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setNewZoneColor(color)}
                    className={`w-6 h-6 rounded border-2 transition-all ${
                      newZoneColor === color ? 'border-foreground scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                    aria-label={`Couleur ${color}`}
                  />
                ))}
              </div>
            </div>

            <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-blue-800 dark:text-blue-300 text-xs">
                Pensez à rattacher chaque table à une zone dans la page{' '}
                <a href="/dashboard/tables" className="underline font-medium">Gestion des tables</a> :
                colonne <strong>"Zone réservable"</strong> en vue liste, ou bouton <em>Modifier</em> en vue grille.
                Les tables combinées héritent automatiquement de la zone commune à toutes leurs sous-tables.
              </AlertDescription>
            </Alert>
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!zoneToDelete} onOpenChange={(open) => !open && setZoneToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la zone "{zoneToDelete?.name}" ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les tables actuellement rattachées à cette zone passeront en "sans zone".
              Les réservations existantes ne sont pas affectées. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
